import { useState } from 'react';
import { Button } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import CardModal from './card-modal';
import { useTranslation } from 'react-i18next';
import { useAssignReservationClient, useUpdateReservation } from '@/hooks/api-hooks';
import BusinessSelector from './business-selector';
import { Business, Reservation } from '@turizi/shared';
import { localTenant } from '@/services/session-service';


interface Props {
    reservation: Reservation;
}

export default function ForceReservationData({ reservation }: Props) {
    const { t } = useTranslation();
    const tenant = localTenant();

    const [dateRange, setDateRange] = useState<[string | null, string | null]>([reservation.start_day, reservation.end_day]);
    const [client, setClient] = useState<Business | null>(reservation?.client_business);

    const [isSaving, setIsSaving] = useState(false);
    const [localOpened, setLocalOpened] = useState(true);

    const assignReservationClient = useAssignReservationClient();
    const updateReservation = useUpdateReservation();

    const hasClient = !!(reservation?.client_business);
    const opened = (!reservation?.start_day ||
        !reservation?.end_day ||
        !hasClient) && localOpened;

    const handleSave = async () => {
        const [start, end] = dateRange;
        if (!start || !end || !client?.id) return;
        setIsSaving(true);
        try {
            await updateReservation.mutateAsync({
                id: reservation.id,
                start_day: start,
                end_day: end,

            });
            if (hasClient) {
                return;
            }
            await assignReservationClient.mutateAsync({
                id: reservation.id,
                clientId: client.id,
                clientType: client.type
            });
            // Hide locally so it doesn't pop up again while React Query is loading/invalidating
            setLocalOpened(false);
        } catch (error) {
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <CardModal
            header={<div className='font-bold'>{t('reservation')} #{reservation.id}</div>}
            opened={opened}
            size='full'
            closeOnEscape={false}
            withCloseButton={false}
            onClose={() => { }}
        >
            <div className="flex flex-col items-center gap-4">
                <div className='flex w-full gap-2 items-center'>
                    <BusinessSelector
                        size='lg'
                        exclude={tenant.businesses.map(b => b.id)}
                        disabled={hasClient || isSaving}
                        selected={client}
                        className="font-bold"
                        onSelect={setClient}
                    />
                </div>
                <DatePicker
                    highlightToday={true}
                    size='md'
                    className='border border-gray-200 p-2 rounded-xl'
                    numberOfColumns={2}
                    type="range"
                    value={dateRange}
                    onChange={setDateRange}
                />
                <div className="w-full mt-2">
                    <Button
                        color="black"
                        radius="xl"
                        size='sm'
                        onClick={handleSave}
                        disabled={!dateRange[0] || !dateRange[1] || isSaving || !client}
                        loading={isSaving}
                        fullWidth
                    >
                        {t('save')}
                    </Button>
                </div>
            </div>
        </CardModal>
    );
}
